/* eslint-disable react/no-unknown-property */
import { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

interface PathLineProps {
  waypoints: React.RefObject<THREE.Vector3[]>;
  avatarPosition: React.RefObject<THREE.Vector3>;
}

const MAX_POINTS = 50;

export default function PathLine({ waypoints, avatarPosition }: PathLineProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const lineRef = useRef<any>(null!);
  const dotRef = useRef<THREE.Mesh>(null!);
  const geoRef = useRef<THREE.BufferGeometry>(null!);

  // Pre-allocate buffer
  const posArray = useRef(new Float32Array(MAX_POINTS * 3)).current;

  useFrame(() => {
    if (!geoRef.current || !lineRef.current) return;

    const wps = waypoints.current ?? [];
    const avatar = avatarPosition.current;

    if (!avatar || wps.length === 0) {
      lineRef.current.visible = false;
      if (dotRef.current) dotRef.current.visible = false;
      return;
    }

    // Build points: avatar position + remaining waypoints
    let idx = 0;
    posArray[idx++] = avatar.x;
    posArray[idx++] = 0.08;
    posArray[idx++] = avatar.z;

    const count = Math.min(wps.length, MAX_POINTS - 1);
    for (let i = 0; i < count; i++) {
      posArray[idx++] = wps[i].x;
      posArray[idx++] = 0.08;
      posArray[idx++] = wps[i].z;
    }

    const totalPoints = count + 1;
    geoRef.current.setAttribute(
      "position",
      new THREE.BufferAttribute(posArray.slice(0, totalPoints * 3), 3)
    );
    geoRef.current.setDrawRange(0, totalPoints);
    geoRef.current.attributes.position.needsUpdate = true;
    lineRef.current.computeLineDistances();
    lineRef.current.visible = true;

    // Destination dot
    if (dotRef.current && wps && wps.length > 0) {
      const last = wps[wps.length - 1];
      dotRef.current.position.set(last.x, 0.08, last.z);
      dotRef.current.visible = true;
    }
  });

  return (
    <group>
      <line ref={lineRef}>
        <bufferGeometry ref={geoRef} />
        <lineDashedMaterial
          color="#168aff"
          transparent
          opacity={0.6}
          dashSize={0.3}
          gapSize={0.15}
        />
      </line>
      <mesh ref={dotRef}>
        <sphereGeometry args={[0.1, 8, 8]} />
        <meshBasicMaterial color="#168aff" transparent opacity={0.7} />
      </mesh>
    </group>
  );
}
